import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    MSI_TYPE_SCALE,
    usesAppStyleGrammar,
} from './msiStyleTokens';
import { workspacePanelElevationSx } from './workspaceChromeStyles';

const TONES = Object.freeze({
    success: Object.freeze({
        foreground: MSI_PALETTE.success,
        background: MSI_PALETTE.successBg,
        border: MSI_COMPONENT_COLORS.semanticBorders.success,
    }),
    warning: Object.freeze({
        foreground: MSI_PALETTE.warning,
        background: MSI_PALETTE.warningBg,
        border: MSI_COMPONENT_COLORS.semanticBorders.warning,
    }),
    danger: Object.freeze({
        foreground: MSI_PALETTE.danger,
        background: MSI_PALETTE.dangerBg,
        border: MSI_COMPONENT_COLORS.semanticBorders.danger,
    }),
    info: Object.freeze({
        foreground: MSI_PALETTE.info,
        background: MSI_PALETTE.infoBg,
        border: MSI_COMPONENT_COLORS.semanticBorders.info,
    }),
    neutral: Object.freeze({
        foreground: MSI_PALETTE.ink500,
        background: MSI_PALETTE.surfaceSubtle,
        border: MSI_PALETTE.lineDark,
    }),
});

export function getWatchlistMsiTone(theme, tone = 'neutral') {
    if (!usesAppStyleGrammar(theme)) return null;
    return TONES[tone] || TONES.neutral;
}

export const watchlistPanelSx = {
    ...workspacePanelElevationSx,
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const watchlistInsetPanelSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_PALETTE.surfaceSubtle : undefined
    ),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const watchlistSecondaryControlSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.lineDark : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink700 : undefined),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    '&:hover': {
        borderColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.secondaryHoverBorder : undefined
        ),
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.secondaryHover : undefined
        ),
        color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink900 : undefined),
    },
};

export const watchlistPopoverPaperSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '0 8px 24px rgba(12, 23, 38, 0.12)' : undefined
    ),
};

export function watchlistSemanticFrameSx(tone = 'neutral') {
    return {
        borderColor: (theme) => getWatchlistMsiTone(theme, tone)?.border,
        borderRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
        ),
        backgroundColor: (theme) => getWatchlistMsiTone(theme, tone)?.background,
        backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
        boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
        backdropFilter: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    };
}

export function watchlistSemanticBadgeSx(tone = 'neutral') {
    return {
        border: (theme) => (
            usesAppStyleGrammar(theme) ? `1px solid ${getWatchlistMsiTone(theme, tone).border}` : undefined
        ),
        borderRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.statusBadge}px` : undefined
        ),
        backgroundColor: (theme) => getWatchlistMsiTone(theme, tone)?.background,
        color: (theme) => getWatchlistMsiTone(theme, tone)?.foreground,
        fontSize: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontSize : undefined
        ),
        fontWeight: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontWeight : undefined
        ),
        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
        textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
        boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    };
}

export const watchlistCompactLabelSx = {
    fontSize: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontSize : undefined
    ),
    fontWeight: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontWeight : undefined
    ),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
};

export const watchlistPortfolioQuantityInputSx = {
    width: 112,
    '& .MuiInputBase-root': {
        height: MSI_GEOMETRY.compactControlHeight,
        borderRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.input}px` : undefined
        ),
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined
        ),
        fontSize: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableBody.fontSize : undefined
        ),
        fontWeight: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableBody.fontWeight : undefined
        ),
    },
    '& .MuiFormHelperText-root': {
        marginInline: 0,
        fontSize: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.helper.fontSize : undefined
        ),
    },
};

export const watchlistTableFrameSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    '& .MuiTableCell-head': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.tableHeader : undefined
        ),
        color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink700 : undefined),
        fontSize: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontSize : undefined
        ),
        fontWeight: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontWeight : undefined
        ),
        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
        textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
    },
};
